/**
 * ForecastByRep.tsx — Forecast programme F5 (register row 73): the By rep
 * table, spec §3.7 under ADOPTION §18.7, rendered from forecastByRep.ts's
 * model (the rules are stated there). It replaces the pre-F5 "Attainment by
 * rep" grid with that grid's content kept: the won-cell drill (§33 W-4), the
 * fence counts, the non-GBP note, the Total target and the empty state.
 *
 * THE WRITE SURFACE is the existing quota grain (§18.7), FRONTEND only:
 *   - "Set target" — inline, beside U-5's "No target set" in a row with no
 *     target: ONE PUT /v1/sales/quotas for that employee and the RESPONSE's
 *     period (never the picker's state, the §33 W-2 rule).
 *   - "Set targets" — the Settings → Sales table (QuotasTable, exported, not
 *     forked) in a dialog for the same period: set, change or clear.
 * The server's owner/admin money gate is the authority, exactly as on the
 * Settings card: a refusal is surfaced verbatim through apiErrorMessage.
 * After every successful write the tab refetches the period (spec §3.8:
 * setting a target updates the hero and the table without a reload).
 *
 * U-5 COLUMNS (RULED 2026-10-01, Klemen; spec §3.7's last bullet): the
 * Attainment column is GONE — the Target cell carries the same figure ("{pct}%
 * of {target}"), and U-5's "No target set" stays untouched in that cell. The
 * Overrides column renders only while at least one row has an override
 * (`showOverrides`); when every row reads 0 it is dropped.
 * "Link owners ›" on the Unattributed row is F4b (register row 81).
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { apiErrorMessage } from '../../../../api/client'
import type { SalesForecast } from '../../../../api/types'
import { parsePoundsToPence, money } from '../../../../lib/money'
import { success, warning, danger, ink, surface, overlay, forecastAttributionSplit } from '../../../../theme/tokens'
import { EmptyState, LoadingState, ErrorState, Avatar } from '../../DashboardShared'
import { useAsync, fetchDirectory, fetchSalesQuotas, upsertSalesQuota } from '../../api'
import { QuotasTable } from '../settings/QuotasCard'
import { quotaInputValid } from '../settings/quotas'
import { type SalesPeriod, periodLabel } from './salesPeriod'
import { type ReportsDrill } from './reportsDrill'
import {
  fencedDealCount, forecastNonGbpNote, overrideColumn, overridePairsLine, wonCellDrill, wonCellDrills, ABSENT,
} from './forecastView'
import { byRepModel, shareText, COUNTS_FOR_NO_ONE, UNATTRIBUTED_LABEL, type Pace, type ByRepModel } from './forecastByRep'
import { OVER_TARGET } from './forecastHero'

const PACE_FILL: Record<Pace, string> = { on: success.DEFAULT, behind: warning.DEFAULT, far: danger.DEFAULT }
const PACE_WORD: Record<Pace, string> = { on: 'on pace', behind: 'behind pace', far: 'well behind pace' }

export function ForecastByRep({ data: f, period, onDrill, onChanged }: {
  data: SalesForecast
  period: SalesPeriod
  onDrill?: (drill: ReportsDrill) => void
  onChanged?: () => void
}) {
  const note = forecastNonGbpNote(f.excludedNonGbp)
  const m = byRepModel(f)
  // The response's own period: every write targets what the table shows.
  const wirePeriod: SalesPeriod = { kind: f.periodKind, start: f.periodStart }
  const [dialog, setDialog] = useState(false)
  const label = periodLabel(period)

  return (
    <section className="k-card" aria-label="By rep">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">By rep</div>
          <div className="k-card-sub">{`Won in ${label} · open pipeline as it stands today`}</div>
        </div>
        <div className="flex items-center gap-3">
          {f.totalTargetPence !== null && (
            <div className="text-12 text-ink-muted">Total target {money(f.totalTargetPence / 100)}</div>
          )}
          <button type="button" className="k-btn-secondary" onClick={() => setDialog(true)}>Set targets</button>
        </div>
      </div>
      {note && (
        <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">{note}</div>
      )}
      {f.rows.length === 0 ? (
        <EmptyState message="No one to measure yet"
          hint="A row appears for each employee with a target or a won deal in this period. Set targets with the button above, and link deals to employees so their wins count." />
      ) : (
        <ByRepTable f={f} m={m} label={label} wirePeriod={wirePeriod} onDrill={onDrill} onChanged={onChanged} />
      )}
      {dialog && <SetTargetsDialog period={wirePeriod} onClose={() => setDialog(false)} onSaved={onChanged} />}
    </section>
  )
}

function ByRepTable({ f, m, label, wirePeriod, onDrill, onChanged }: {
  f: SalesForecast; m: ByRepModel; label: string; wirePeriod: SalesPeriod
  onDrill?: (drill: ReportsDrill) => void; onChanged?: () => void
}) {
  return (
    <table className="closr-table sales-table w-full" data-won-total={m.wonTotalPence} data-page-won={m.pageWonPence}
      data-won-reconciles={m.wonReconciles} data-elapsed={m.elapsedPct ?? ''}>
      <thead>
        <tr>
          <th className="text-left">Rep</th>
          {/* §26 F-2a: the population is NAMED — linked employees, then the
              Unattributed row, so the column adds up to the page's won. */}
          <th className="text-left" title="Linked employees, then the unattributed won value">{`Won ${label}`}</th>
          <th className="text-left">Target</th>
          <th className="text-left">Open pipeline</th>
          <th className="text-left">Weighted</th>
          <th className="text-left" title="Each rep's share of the weighted pipeline linked to reps">Share of weighted</th>
          {m.showOverrides && <th className="text-left">Overrides</th>}
        </tr>
      </thead>
      <tbody>
        {m.rows.map(({ row: r, target, won, sharePct }) => (
          <tr key={r.employeeId} data-employee-id={r.employeeId}>
            <td>
              <div className="flex items-center gap-2 text-13 font-medium text-ink-primary">
                <Avatar name={r.employeeName} size={28} />{r.employeeName}
              </div>
            </td>
            <td className="text-13 text-ink-body" data-won-pence={r.wonPence}>
              {/* §33 W-4: a button only while the figure exists. */}
              {wonCellDrills(r) ? (
                <button type="button" data-drill="won" title="Show the deals behind this figure"
                  onClick={() => onDrill?.(wonCellDrill(f, r))}
                  className="text-left cursor-pointer rounded-control hover:underline focus:outline-none">
                  {won.value} <span className="text-12 text-ink-muted">· {won.count}</span>
                </button>
              ) : won.won ? (
                <>{won.value} <span className="text-12 text-ink-muted">· {won.count}</span></>
              ) : (
                <span className="text-ink-muted">{won.value}</span>
              )}
            </td>
            <td className="text-13" data-target={target.kind} data-pace={target.kind === 'set' ? (target.pace ?? 'none') : undefined}>
              {target.kind === 'none' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-ink-muted">{target.text}</span>
                  <SetTargetInline employeeId={r.employeeId} name={r.employeeName} period={wirePeriod} onChanged={onChanged} />
                </div>
              )}
              {target.kind === 'zero' && <span className="font-semibold text-ink-primary">{target.text}</span>}
              {target.kind === 'set' && (
                <div className="flex flex-col gap-1" style={{ minWidth: 140 }}>
                  {/* Row 90: at or under 100 the cell is exactly what it was;
                      above it the words sit after the figure and in the bar's name. */}
                  {target.over ? (
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-ink-primary">{target.text}</span>
                      <span className="k-badge" style={{ background: success.bg, color: success.text }} data-over-target>{OVER_TARGET}</span>
                    </span>
                  ) : (
                    <span className="font-semibold text-ink-primary">{target.text}</span>
                  )}
                  <div className={`rounded-full bg-surface-control overflow-hidden${target.tickPct !== null ? ' relative' : ''}`} style={{ height: 4 }}
                    role="img" aria-label={`${target.text}${target.over ? ', over target' : ''}${target.pace ? `, ${PACE_WORD[target.pace]}` : ''}`}>
                    <div className="h-full rounded-full" style={{ width: `${target.barPct}%`, background: target.pace ? PACE_FILL[target.pace] : ink.faint }} />
                    {/* The target, inside a full fill: a cut in the card's own ground at 100 ÷ pct. */}
                    {target.tickPct !== null && (
                      <div className="absolute" style={{ top: 0, bottom: 0, left: `${target.tickPct}%`, width: 2, background: surface.card }} data-target-tick />
                    )}
                  </div>
                </div>
              )}
            </td>
            <td className="text-13 text-ink-body">
              {money(r.openPence / 100)} <span className="text-12 text-ink-muted">· {fencedDealCount(r.openCount, r.openExcludedNonGbp)}</span>
            </td>
            <td className="text-13 text-ink-body">{money(r.weightedPence / 100)}</td>
            <td className="text-13 text-ink-body" data-share={sharePct === null ? '' : sharePct.toFixed(2)}>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-full bg-surface-control overflow-hidden" style={{ height: 6, minWidth: 48 }} aria-hidden="true">
                  <div className="h-full rounded-full" style={{ width: `${sharePct ?? 0}%`, background: forecastAttributionSplit.attributed }} />
                </div>
                <span className="whitespace-nowrap">{shareText(sharePct)}</span>
              </div>
            </td>
            {/* 2.3c: count · Σ gap; the (rep → override) pairs ride the title. */}
            {m.showOverrides && (
              <td className={r.overriddenCount === 0 ? 'text-13 text-ink-muted' : 'text-13 text-ink-body'}
                title={r.overriddenCount ? overridePairsLine(r.overridePairs) : undefined} data-overridden={r.overriddenCount}>
                {overrideColumn(r.overriddenCount, r.overrideGapPence)}
              </td>
            )}
          </tr>
        ))}
        <tr data-row-kind="unattributed" data-won-pence={f.unattributedWonPence}>
          <td>
            <div className="flex items-center gap-2 text-13 font-medium text-ink-primary">
              <span className="inline-block rounded-full border border-dashed border-edge-control flex-shrink-0" style={{ width: 28, height: 28 }} aria-hidden="true" />
              {UNATTRIBUTED_LABEL}
            </div>
          </td>
          <td className="text-13 text-ink-body">
            {m.unattributed.won.won
              ? <>{m.unattributed.won.value} <span className="text-12 text-ink-muted">· {m.unattributed.won.count}</span></>
              : <span className="text-ink-muted">{m.unattributed.won.value}</span>}
          </td>
          <td className="text-13 text-ink-muted">{COUNTS_FOR_NO_ONE}</td>
          <td className="text-13 text-ink-muted">{ABSENT}</td>
          <td className="text-13 text-ink-muted">{ABSENT}</td>
          <td className="text-13 text-ink-muted">{ABSENT}</td>
          {m.showOverrides && <td className="text-13 text-ink-muted">{ABSENT}</td>}
        </tr>
      </tbody>
    </table>
  )
}

/** "Set target" for one rep: a link-styled button that opens a pounds input
 *  in place. ONE PUT on save; blank or invalid cannot be sent. */
function SetTargetInline({ employeeId, name, period, onChanged }: {
  employeeId: string; name: string; period: SalesPeriod; onChanged?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { if (open) input.current?.focus() }, [open])

  if (!open) {
    return (
      <button type="button" className="text-13 font-medium text-accent hover:underline" aria-label={`Set target for ${name}`}
        onClick={() => setOpen(true)}>
        Set target
      </button>
    )
  }
  const pence = value.trim() === '' ? null : parsePoundsToPence(value)
  const invalid = !quotaInputValid(value)
  async function save() {
    if (pence === null || busy) return
    setBusy(true)
    try {
      await upsertSalesQuota({ employeeId, periodKind: period.kind, periodStart: period.start, targetPence: pence })
      toast.success(`Target saved for ${periodLabel(period)}`)
      setOpen(false)
      setValue('')
      onChanged?.()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save target'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-12 text-ink-muted">£</span>
      <input ref={input} type="text" inputMode="decimal" value={value} placeholder="Target"
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); void save() }
          if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setValue('') }
        }}
        aria-label={`${name} target for ${periodLabel(period)}`} aria-invalid={invalid || undefined}
        className={'w-28 px-2 py-1 border border-edge-control rounded-control text-13 text-ink-primary text-right' + (invalid ? ' border-danger' : '')} />
      <button type="button" className="k-btn" disabled={pence === null || busy} onClick={() => void save()}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="k-btn-secondary" disabled={busy} onClick={() => { setOpen(false); setValue('') }}>Cancel</button>
    </span>
  )
}

/** "Set targets": the Settings → Sales table for the response's period, in a
 *  dialog. Each row is its own write (set, change, clear). */
function SetTargetsDialog({ period, onClose, onSaved }: { period: SalesPeriod; onClose: () => void; onSaved?: () => void }) {
  const roster = useAsync(() => fetchDirectory({ status: 'active' }), [])
  const quotas = useAsync(() => fetchSalesQuotas({ periodKind: period.kind, periodStart: period.start }), [period.kind, period.start])
  const loading = roster.loading || quotas.loading
  const error = roster.error ?? quotas.error
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const title = `Set targets for ${periodLabel(period)}`
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={title} className="k-card"
        style={{ width: 'min(640px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{title}</div>
            <div className="text-12 text-ink-muted mt-1">
              What each employee is expected to win in the period, in GBP. Leave a target blank for no target; 0 is a real target of zero.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5">
          {loading && <LoadingState rows={3} />}
          {!loading && error && <ErrorState message={error} />}
          {!loading && !error && (
            <QuotasTable period={period} roster={roster.data!} quotas={quotas.data!.items} onSaved={onSaved} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
